import { useEffect, useState } from "react";
import { Users, MapPin, GraduationCap, Recycle, TreePine } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, Tooltip } from "recharts";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Container, Eyebrow, Reveal } from "../components/common";
import { api, trackVisit } from "../lib/api";

const CountCard = ({ icon: Icon, value, label, suffix = "", testid }) => (
  <div data-testid={testid} className="p-8 rounded-2xl bg-rc-card border border-rc-border hover:border-rc-sage/40 transition-all duration-300">
    <Icon className="w-6 h-6 text-rc-sage" strokeWidth={1.5} />
    <div className="mt-6 font-serif text-4xl lg:text-5xl font-light text-stone-100">
      {value}
      <span className="text-2xl text-rc-sage">{suffix}</span>
    </div>
    <div className="mt-2 text-sm text-stone-400 font-light">{label}</div>
  </div>
);

export default function Tablero() {
  const [ind, setInd] = useState(null);

  useEffect(() => {
    trackVisit("/tablero", "tablero");
    api.get("/indicators").then((r) => setInd(r.data)).catch(() => {});
  }, []);

  const franjas = ind?.franjas || [];

  return (
    <div className="bg-rc-bg min-h-screen">
      <Navbar />
      <section className="relative pt-36 pb-20 overflow-hidden rc-grain">
        <div className="absolute -top-20 right-0 w-[30rem] h-[30rem] rounded-full bg-rc-forest/15 blur-[120px]" />
        <Container className="relative z-10">
          <Reveal>
            <Eyebrow>Tablero público de impacto</Eyebrow>
            <h1 className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-stone-100">
              Lo que el territorio <span className="text-rc-sage italic">ya mueve</span>
            </h1>
            <p className="mt-6 max-w-2xl text-stone-400 font-light leading-relaxed">
              Indicadores abiertos del ecosistema Raíz Commerce. Datos del NODO Serrano, actualizados por el
              equipo. Transparencia por diseño — en línea con el Art. 38 de la LGEC.
            </p>
            {ind?.nota && (
              <div className="mt-6 inline-block text-sm font-mono px-4 py-2 rounded-full bg-rc-forest/20 text-rc-sage border border-rc-forest/40">
                {ind.nota}
              </div>
            )}
          </Reveal>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Reveal delay={0}><CountCard testid="metric-actores" icon={Users} value={ind?.actores_convocados ?? "—"} label="Actores convocados y en formación" /></Reveal>
            <Reveal delay={0.08}><CountCard testid="metric-municipios" icon={MapPin} value={ind?.municipios_activos ?? "—"} label="Municipios activos" /></Reveal>
            <Reveal delay={0.16}><CountCard testid="metric-cursos" icon={GraduationCap} value={ind?.cursos_impartidos ?? "—"} label="Cursos impartidos" /></Reveal>
            <Reveal delay={0.24}><CountCard testid="metric-residuos" icon={Recycle} value={ind?.residuos_compostados_ton ?? "—"} suffix=" t" label="Residuos compostados" /></Reveal>
          </div>

          <div className="mt-6 grid lg:grid-cols-3 gap-6">
            <Reveal>
              <CountCard testid="metric-arboles" icon={TreePine} value={(ind?.arboles_plantados ?? 0).toLocaleString("es-MX")} label="Árboles nativos plantados" />
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-2">
              <div className="h-full p-8 rounded-2xl bg-rc-card border border-rc-border" data-testid="reforestacion-chart">
                <div className="rc-eyebrow">Cobertura de reforestación por franja</div>
                <h3 className="mt-3 font-serif text-xl text-stone-100">De la alta montaña al manglar</h3>
                <div className="mt-6 h-64">
                  {franjas.length > 0 && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={franjas} layout="vertical" margin={{ left: 20, right: 30 }}>
                        <XAxis type="number" domain={[0, 100]} tick={{ fill: "#68706b", fontSize: 12 }} axisLine={false} tickLine={false} unit="%" />
                        <YAxis type="category" dataKey="nombre" width={120} tick={{ fill: "#a1a8a3", fontSize: 12 }} axisLine={false} tickLine={false} />
                        <Tooltip cursor={{ fill: "rgba(130,179,133,0.08)" }} contentStyle={{ background: "#181d1a", border: "1px solid #252d28", borderRadius: 8, color: "#f3f4f2" }} formatter={(v) => [`${v}%`, "Cobertura"]} />
                        <Bar dataKey="cobertura_pct" radius={[0, 6, 6, 0]} barSize={20}>
                          {franjas.map((_, i) => (
                            <Cell key={i} fill={["#1d3a28", "#2e5b3e", "#4f8a65", "#82b385", "#c86d51", "#e0a96d", "#4f8a65", "#2e5b3e"][i % 8]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>
      <Footer />
    </div>
  );
}
